import { Flex, Paper, Stack } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { IconCheck } from '@tabler/icons-react';

import { CurrencyConvertor } from '~/components/utils/formatters';
import { PrimaryButton } from '~/domains/common/components/Button/Button';
import { Text } from '~/domains/common/components/Text/Text';
import { PaymentType } from '~/common/enums/payments.enum';
import dt from '~/testing';

import { useWalletBalance } from '../hooks/useWalletBalance';

interface WithdrawalCompletedProps {
  onClose: React.MouseEventHandler;
  paymentType: PaymentType;
  withdrawalAmount: number;
}

export function WithdrawalCompleted({
  onClose,
  paymentType,
  withdrawalAmount,
}: WithdrawalCompletedProps) {
  const { data: balance } = useWalletBalance();
  const { t } = useTranslation('payments');

  return (
    <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={40} maw={450} mx="auto">
      <Text c="gray.9" variant="web-headline-large" align="center">
        {t('withdrawal.completed.title')}
      </Text>

      <Paper /* eslint-disable-line local-rules/use-theme-tokens */ p={40} pt={24}>
        <Stack w="100%" align="center" /* eslint-disable-line local-rules/use-theme-tokens */ spacing={24}>
          <Flex
            w={48}
            h={48}
            mx="auto"
            bg="blue.3"
            sx={{ borderRadius: '100%' }}
            align="center"
            justify="center"
          >
            <IconCheck color="white" size={32} />
          </Flex>
          <Text
            data-test-id={dt.payments.withdrawal.withdrawnAmountText}
            color="black"
            variant="web-headline-xxl"
          >
            {CurrencyConvertor(withdrawalAmount, true)}
          </Text>
          <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} align="center">
            <Text
              data-test-id={dt.payments.withdrawal.transferConfirmationText}
              color="gray.9"
              variant="web-subhead-medium"
            >
              {t('withdrawal.completed.fundsWithdrawn', {
                paymentType:
                  paymentType === PaymentType.CARD
                    ? t('paymentTypes.DEBIT_CARD')
                    : t(`paymentTypes.${paymentType}`),
              })}
            </Text>
            <Text
              data-test-id={dt.payments.withdrawal.remainingBalanceText}
              color="gray.4"
              variant="web-subhead-medium"
            >
              {t('withdrawal.completed.newBalance', {
                amount: CurrencyConvertor(balance?.availableBalance, true),
              })}
            </Text>
          </Stack>
        </Stack>
      </Paper>

      <PrimaryButton
        data-test-id={dt.payments.withdrawal.continueButton}
        size="lg"
        type="button"
        fullWidth
        onClick={onClose}
      >
        {t('withdrawal.completed.continue')}
      </PrimaryButton>
    </Stack>
  );
}
